import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Category } from '@/types/database';

interface CategoriesProps {
  categories: Category[];
  isLoading?: boolean;
}

export function Categories({ categories, isLoading }: CategoriesProps) {
  // Demo categories if none in database
  const displayCategories = categories.length > 0 ? categories : [
    {
      id: '1',
      name: 'Sillas Ergonómicas',
      slug: 'sillas-ergonomicas',
      description: 'Para largas jornadas de trabajo con máximo ajuste y comodidad',
      image_url: 'https://images.unsplash.com/photo-1580480055273-228ff5388ef8?w=400&h=300&fit=crop',
    },
    {
      id: '2',
      name: 'Sillas Gaming',
      slug: 'sillas-gaming',
      description: 'Estilo gaming con fundamentos ergonómicos reales',
      image_url: 'https://images.unsplash.com/photo-1598550476439-6847785fcea6?w=400&h=300&fit=crop',
    },
    {
      id: '3',
      name: 'Sillas Ejecutivas',
      slug: 'sillas-ejecutivas',
      description: 'Diseño premium para despachos y oficinas de alto nivel',
      image_url: 'https://images.unsplash.com/photo-1519219788971-8d9797e0928e?w=400&h=300&fit=crop',
    },
    {
      id: '4',
      name: 'Sillas Económicas',
      slug: 'sillas-economicas',
      description: 'La mejor relación calidad-precio bajo 200€',
      image_url: 'https://images.unsplash.com/photo-1589364203596-45afcc9e3b8a?w=400&h=300&fit=crop',
    },
    {
      id: '5',
      name: 'Sillas para Dolor Lumbar',
      slug: 'sillas-dolor-lumbar',
      description: 'Especializadas en soporte lumbar para problemas de espalda',
      image_url: 'https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?w=400&h=300&fit=crop',
    },
    {
      id: '6',
      name: 'Sillas para Personas Altas',
      slug: 'sillas-personas-altas',
      description: 'Diseñadas para estaturas superiores a 180cm',
      image_url: 'https://images.unsplash.com/photo-1505843490538-5133c6c7d0e1?w=400&h=300&fit=crop',
    },
  ] as Category[];

  if (isLoading) {
    return (
      <section className="py-16">
        <div className="container-wide">
          <div className="mb-10">
            <h2 className="font-display text-3xl font-bold">Categorías Populares</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse overflow-hidden">
                <div className="aspect-[4/3] bg-muted" />
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16">
      <div className="container-wide">
        {/* Header */}
        <div className="mb-10 text-center">
          <h2 className="font-display text-3xl font-bold">Categorías Populares</h2>
          <p className="mt-2 text-muted-foreground">
            Explora nuestras guías por tipo de silla
          </p>
        </div>

        {/* Grid */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {displayCategories.slice(0, 6).map((category) => (
            <Link 
              key={category.id} 
              to={`/categoria/${category.slug}`}
              className="group"
            >
              <Card className="card-hover overflow-hidden border-0 shadow-md">
                <div className="relative aspect-[4/3] overflow-hidden">
                  {/* Image */}
                  <img
                    src={category.image_url || 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=300&fit=crop'}
                    alt={category.name}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  
                  {/* Content */}
                  <CardContent className="absolute inset-x-0 bottom-0 p-5 text-white">
                    <h3 className="mb-1 font-display text-xl font-semibold">
                      {category.name}
                    </h3>
                    <p className="mb-3 line-clamp-2 text-sm text-white/80">
                      {category.description}
                    </p>
                    <span className="inline-flex items-center gap-1 text-sm font-medium text-white transition-transform group-hover:translate-x-1">
                      Ver guías <ArrowRight className="h-4 w-4" />
                    </span>
                  </CardContent>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}